/**
 * Canonical notification-trigger IDs, mirroring the values the backend stores in
 * `app_notifications.notification_trigger_id`.
 *
 * These IDs are the lookup keys for the CTA/description maps in
 * `lib/utils/notificationHelpers.ts`. They were previously hand-written string
 * literals there, and 22 of them were misspelled — the lookup missed, so the
 * task card silently lost its action button and fell back to a sentence that
 * omitted the resource name (DELTA-2317). Keying off this object instead makes a
 * typo a build error.
 *
 * Note the IDs are NOT uniformly formatted: the CAPA/CAP family is UPPER_SNAKE
 * while everything else is kebab-case, and a few names have drifted from their
 * ID (`supplyChainCollaboration*` is stored as `cascade-mapping-collaborator-*`).
 * Copy the value verbatim from the source of truth rather than deriving it from
 * the name.
 *
 * Source of truth: `backend/src/types/notification-triggers/index.ts`, seeded
 * into `notification_triggers` by
 * `backend/src/setup/migrations-snapshot/202605120100-initial-seeds-006.ts` plus
 * later per-trigger migrations. This object lists only the triggers the frontend
 * renders — add an entry when the frontend starts handling a new one.
 */
export const NOTIFICATION_TRIGGER_ID = {
  auditRequested: 'audit-requested',
  amforiSyncReport: 'amfori-sync-report',
  bomBulkImportCompleted: 'bom-bulk-import-completed',
  capCollaboratorAdded: 'CAP_COLLABORATOR_ADDED',
  capCollaboratorAddedGeneral: 'CAP_COLLABORATOR_ADDED_GENERAL',
  capCollaboratorRemoved: 'CAP_COLLABORATOR_REMOVED',
  capCollaboratorRemovedGeneral: 'CAP_COLLABORATOR_REMOVED_GENERAL',
  capCommentAdded: 'CAP_COMMENT_ADDED',
  capCommentAddedGeneral: 'CAP_COMMENT_ADDED_GENERAL',
  capDueSoon: 'CAP_DUE_SOON',
  capOverdue: 'CAP_OVERDUE',
  capResponsibleCompanyAdded: 'CAP_RESPONSIBLE_COMPANY_ADDED',
  capUserAssigneeAdded: 'CAP_USER_ASSIGNEE_ADDED',
  capUserAssigneeAddedGeneral: 'CAP_USER_ASSIGNEE_ADDED_GENERAL',
  capUserAssigneeRemoved: 'CAP_USER_ASSIGNEE_REMOVED',
  capUserAssigneeRemovedGeneral: 'CAP_USER_ASSIGNEE_REMOVED_GENERAL',
  capaCollaboratorAdded: 'CAPA_COLLABORATOR_ADDED',
  capaCollaboratorAddedGeneral: 'CAPA_COLLABORATOR_ADDED_GENERAL',
  capaCollaboratorRemoved: 'CAPA_COLLABORATOR_REMOVED',
  capaCollaboratorRemovedGeneral: 'CAPA_COLLABORATOR_REMOVED_GENERAL',
  capaCommentAdded: 'CAPA_COMMENT_ADDED',
  capaCommentAddedGeneral: 'CAPA_COMMENT_ADDED_GENERAL',
  capaDueSoon: 'CAPA_DUE_SOON',
  capaOverdue: 'CAPA_OVERDUE',
  capaRemediationAdded: 'CAPA_REMEDIATION_ADDED',
  capaRemediationRemoved: 'CAPA_REMEDIATION_REMOVED',
  capaResponsibleCompanyAdded: 'CAPA_RESPONSIBLE_COMPANY_ADDED',
  capaStatusUpdateChangesRequested: 'CAPA_STATUS_UPDATE_CHANGES_REQUESTED',
  capaStatusUpdateChangesRequestedGeneral:
    'CAPA_STATUS_UPDATE_CHANGES_REQUESTED_GENERAL',
  capaStatusUpdateDoneGeneral: 'CAPA_STATUS_UPDATE_DONE_GENERAL',
  capaStatusUpdateInProgressGeneral: 'CAPA_STATUS_UPDATE_IN_PROGRESS_GENERAL',
  capaStatusUpdateInReview: 'CAPA_STATUS_UPDATE_IN_REVIEW',
  capaStatusUpdateInReviewGeneral: 'CAPA_STATUS_UPDATE_IN_REVIEW_GENERAL',
  capaUserAssigneeAdded: 'CAPA_USER_ASSIGNEE_ADDED',
  capaUserAssigneeAddedGeneral: 'CAPA_USER_ASSIGNEE_ADDED_GENERAL',
  capaUserAssigneeRemoved: 'CAPA_USER_ASSIGNEE_REMOVED',
  capaUserAssigneeRemovedGeneral: 'CAPA_USER_ASSIGNEE_REMOVED_GENERAL',
  certifiedMaterialsDocumentRevisionRequested:
    'certified-materials-document-revision-requested',
  certifiedMaterialsOrderSubmittedForReview:
    'certified-materials-order-submitted-for-review',
  certifiedMaterialsPoImportCompleted:
    'certified-materials-po-import-completed',
  certifiedMaterialsSupplierAssignedToOrders:
    'certified-materials-supplier-assigned-to-orders',
  certifiedMaterialsSupplierNewEvidenceRequested:
    'certified-materials-supplier-new-evidence-requested',
  certifiedMaterialsSupplierReminderSent:
    'certified-materials-supplier-reminder-sent',
  claimRejected: 'claim-rejected',
  companyJoinRequestCreated: 'company-join-request-created',
  companyJoinRequestDecided: 'company-join-request-decided',
  // The emitter writes the trigger id into the resource-type column too — see
  // the `'company-linked'` alias in `resourceTypes.ts`.
  companyLinked: 'company-linked',
  cpmCollaboratorAccepted: 'cpm-collaborator-accepted',
  cpmCollaboratorApproved: 'cpm-collaborator-approved',
  cpmCollaboratorChangesRequested: 'cpm-collaborator-changes-requested',
  cpmCollaboratorInReview: 'cpm-collaborator-in-review',
  cpmCollaboratorInvited: 'cpm-collaborator-invited',
  cpmExportCompleted: 'cpm-export-completed',
  cpmExportFailed: 'cpm-export-failed',
  // Renamed from `sc-bulk-import-completed` by migration 202606031400.
  cpmImportCompleted: 'cpm-import-completed',
  documentRequested: 'document-requested',
  documentShared: 'document-shared',
  entityScreeningKharonMatch: 'entity-screening-kharon-match',
  entityScreeningUflpaMatch: 'entity-screening-uflpa-match',
  // Stored plural — the singular form never matched anything.
  networkCompaniesExport: 'network-companies-export',
  nextPlannedAudit: 'next-planned-audit',
  ordersBulkImportCompleted: 'orders-bulk-import-completed',
  ordersExportCompleted: 'orders-export-completed',
  ordersExportFailed: 'orders-export-failed',
  ownCertificatesExpired: 'own-certificates-expired',
  ownCertificatesExpiring: 'own-certificates-expiring',
  ownQuestionnaireDueDateClosing: 'own-questionnaire-due-date-closing',
  productsImported: 'products-imported',
  questionnaireArchived: 'questionnaire-archived',
  // Stored singular ("change"), despite the plural constant name.
  questionnaireChangesRequested: 'questionnaire-change-requested',
  questionnaireCommented: 'questionnaire-commented',
  questionnaireDueDateChanged: 'questionnaire-due-date-changed',
  questionnaireExported: 'questionnaire-exported',
  questionnaireInvitation: 'questionnaire-invitation',
  questionnaireQuestionAdded: 'questionnaire-question-added',
  questionnaireReminder: 'questionnaire-reminder',
  questionnaireResponseSubmitted: 'questionnaire-response-submitted',
  questionnaireReviewCompleted: 'questionnaire-review-completed',
  questionnaireUnarchived: 'questionnaire-unarchived',
  reportsCertificatesComplianceExport: 'reports-certificates-compliance-export',
  reportsTracingOperationsExport: 'reports-tracing-operations-export',
  // Stored "requested-", not "request-".
  requestQuestionnaireDueDateClosing:
    'requested-questionnaire-due-date-closing',
  slcpAdded: 'slcp-added',
  slcpAssessmentAccessRequested: 'slcp-assessment-access-requested',
  slcpAssessmentMissingAccessRequest: 'slcp-assessment-missing-access-request',
  slcpAssessmentShared: 'slcp-assessment-shared',
  // The backend's typo ("companie") is the stored value — do not correct it.
  slcpAssessmentSharedFeatureDisabled:
    'slcp-assessment-shared-to-companie-with-feature-disabled',
  stylesBulkImportCompleted: 'styles-bulk-import-completed',
  stylesExportCompleted: 'styles-export-completed',
  stylesExportFailed: 'styles-export-failed',
  // Supply-chain collaboration is stored under the older cascade-mapping names.
  supplyChainCollaborationAccepted: 'cascade-mapping-collaboration-accepted',
  supplyChainCollaborationRequestReminded:
    'cascade-mapping-collaborator-invitation-reminded',
  supplyChainCollaborationRequested: 'cascade-mapping-collaborator-invited',
  supplyChainCollaborationSubmitted: 'cascade-mapping-collaboration-submitted',
  supplyChainCollaborationChangesRequested:
    'cascade-mapping-collaboration-changes-requested',
  supplyChainCollaborationApproved: 'cascade-mapping-collaboration-approved',
} as const;

export type NotificationTriggerId =
  (typeof NOTIFICATION_TRIGGER_ID)[keyof typeof NOTIFICATION_TRIGGER_ID];

/**
 * Triggers the `notification_triggers` table marks `actionable` — i.e. they land
 * in the Tasks tab and must render a CTA. Everything else is informative.
 * Verified against the seed data and the per-trigger migrations; used by the
 * helper unit tests to assert every one of them resolves to an action link.
 *
 * `cpmImportCompleted` is deliberately excluded: the row is `actionable` (it
 * inherited the type from `sc-bulk-import-completed` when migration 202606031400
 * renamed it in place), but the product treats it as informative and surfaces its
 * error report as an inline link inside the sentence instead of a button.
 */
export const ACTIONABLE_TRIGGER_IDS: readonly NotificationTriggerId[] = [
  NOTIFICATION_TRIGGER_ID.auditRequested,
  NOTIFICATION_TRIGGER_ID.capCollaboratorAdded,
  NOTIFICATION_TRIGGER_ID.capCommentAdded,
  NOTIFICATION_TRIGGER_ID.capCommentAddedGeneral,
  NOTIFICATION_TRIGGER_ID.capDueSoon,
  NOTIFICATION_TRIGGER_ID.capOverdue,
  NOTIFICATION_TRIGGER_ID.capResponsibleCompanyAdded,
  NOTIFICATION_TRIGGER_ID.capUserAssigneeAdded,
  NOTIFICATION_TRIGGER_ID.capaCollaboratorAdded,
  NOTIFICATION_TRIGGER_ID.capaCommentAdded,
  NOTIFICATION_TRIGGER_ID.capaCommentAddedGeneral,
  NOTIFICATION_TRIGGER_ID.capaDueSoon,
  NOTIFICATION_TRIGGER_ID.capaOverdue,
  NOTIFICATION_TRIGGER_ID.capaRemediationAdded,
  NOTIFICATION_TRIGGER_ID.capaRemediationRemoved,
  NOTIFICATION_TRIGGER_ID.capaResponsibleCompanyAdded,
  NOTIFICATION_TRIGGER_ID.capaStatusUpdateChangesRequested,
  NOTIFICATION_TRIGGER_ID.capaStatusUpdateChangesRequestedGeneral,
  NOTIFICATION_TRIGGER_ID.capaStatusUpdateDoneGeneral,
  NOTIFICATION_TRIGGER_ID.capaStatusUpdateInReview,
  NOTIFICATION_TRIGGER_ID.capaStatusUpdateInReviewGeneral,
  NOTIFICATION_TRIGGER_ID.capaUserAssigneeAdded,
  NOTIFICATION_TRIGGER_ID.certifiedMaterialsDocumentRevisionRequested,
  NOTIFICATION_TRIGGER_ID.certifiedMaterialsOrderSubmittedForReview,
  NOTIFICATION_TRIGGER_ID.certifiedMaterialsPoImportCompleted,
  NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierAssignedToOrders,
  NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierNewEvidenceRequested,
  NOTIFICATION_TRIGGER_ID.certifiedMaterialsSupplierReminderSent,
  NOTIFICATION_TRIGGER_ID.claimRejected,
  NOTIFICATION_TRIGGER_ID.companyJoinRequestCreated,
  NOTIFICATION_TRIGGER_ID.cpmCollaboratorChangesRequested,
  NOTIFICATION_TRIGGER_ID.cpmCollaboratorInReview,
  NOTIFICATION_TRIGGER_ID.cpmCollaboratorInvited,
  NOTIFICATION_TRIGGER_ID.cpmExportCompleted,
  NOTIFICATION_TRIGGER_ID.documentRequested,
  NOTIFICATION_TRIGGER_ID.ordersExportCompleted,
  NOTIFICATION_TRIGGER_ID.ownCertificatesExpired,
  NOTIFICATION_TRIGGER_ID.ownCertificatesExpiring,
  NOTIFICATION_TRIGGER_ID.ownQuestionnaireDueDateClosing,
  NOTIFICATION_TRIGGER_ID.questionnaireChangesRequested,
  NOTIFICATION_TRIGGER_ID.questionnaireCommented,
  NOTIFICATION_TRIGGER_ID.questionnaireDueDateChanged,
  NOTIFICATION_TRIGGER_ID.questionnaireExported,
  NOTIFICATION_TRIGGER_ID.questionnaireInvitation,
  NOTIFICATION_TRIGGER_ID.questionnaireQuestionAdded,
  NOTIFICATION_TRIGGER_ID.questionnaireReminder,
  NOTIFICATION_TRIGGER_ID.questionnaireResponseSubmitted,
  NOTIFICATION_TRIGGER_ID.questionnaireUnarchived,
  NOTIFICATION_TRIGGER_ID.reportsCertificatesComplianceExport,
  NOTIFICATION_TRIGGER_ID.reportsTracingOperationsExport,
  NOTIFICATION_TRIGGER_ID.requestQuestionnaireDueDateClosing,
  NOTIFICATION_TRIGGER_ID.slcpAdded,
  NOTIFICATION_TRIGGER_ID.slcpAssessmentAccessRequested,
  NOTIFICATION_TRIGGER_ID.slcpAssessmentMissingAccessRequest,
  NOTIFICATION_TRIGGER_ID.slcpAssessmentShared,
  NOTIFICATION_TRIGGER_ID.stylesExportCompleted,
  NOTIFICATION_TRIGGER_ID.supplyChainCollaborationRequestReminded,
  NOTIFICATION_TRIGGER_ID.supplyChainCollaborationRequested,
  NOTIFICATION_TRIGGER_ID.supplyChainCollaborationSubmitted,
  NOTIFICATION_TRIGGER_ID.supplyChainCollaborationChangesRequested,
];
